import { useEffect, useState, useCallback } from "react";
import api, { API, formatINR, formatDate } from "../lib/api";
import { toast } from "sonner";
import { Wallet2, ChevronDown, ChevronRight, Save, User, HandCoins, FileSpreadsheet, Printer, X, IndianRupee } from "lucide-react";
import { UpiPayModal } from "../components/UpiPayModal";

export default function PersonalAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [payModal, setPayModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bank/personal-accounts", { params: { start: start || undefined, end: end || undefined } });
      setAccounts(data.accounts || []);
      const drafts = {};
      (data.accounts || []).forEach((a) => { drafts[a.user_id] = a.note || ""; });
      setNoteDrafts(drafts);
    } catch { /* ignore */ }
    setLoading(false);
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  async function saveNote(userId) {
    try {
      await api.put(`/bank/personal-accounts/${userId}/note`, { note: noteDrafts[userId] || "" });
      toast.success("Note saved");
    } catch { toast.error("Could not save note"); }
  }

  const [repayFor, setRepayFor] = useState(null);
  const [credits, setCredits] = useState([]);
  const [loadingCredits, setLoadingCredits] = useState(false);

  async function openRepay(a) {
    setRepayFor(a);
    setLoadingCredits(true);
    try {
      const { data } = await api.get("/bank/untallied-credits");
      const owed = a.advance_out || 0;
      // Surface the likely repayment first: exact-amount matches, then closest, then recent.
      const sorted = (data.credits || []).slice().sort((x, y) => {
        const ex = Math.abs((x.credit || 0) - owed) < 0.5 ? 0 : 1;
        const ey = Math.abs((y.credit || 0) - owed) < 0.5 ? 0 : 1;
        if (ex !== ey) return ex - ey;
        return String(y.date || "").localeCompare(String(x.date || ""));
      });
      setCredits(sorted);
    } catch { setCredits([]); }
    setLoadingCredits(false);
  }

  async function recordRepayment(credit) {
    try {
      await api.post(`/bank/transactions/${credit.id}/tally-personal`, {
        person_id: repayFor.user_id, person_name: repayFor.name, repayment: true, note: "Repayment",
      });
      toast.success("Repayment recorded — advance reduced");
      setRepayFor(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not record repayment"); }
  }

  function exportExcel(userId) {
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    window.open(`${API}/bank/personal-accounts/${userId}/export.xlsx?${qs.toString()}`, "_blank");
  }

  function printStatement(a) {
    const period = `${start || "beginning"} to ${end || "today"}`;
    const rows = a.entries.map((e) => `<tr>
      <td>${formatDate(e.date)}</td><td>${e.account || ""}</td><td>${(e.description || "").replace(/</g, "&lt;")}</td>
      <td>${e.repayment ? "Repayment" : e.kind === "advance" ? "Advance/Loan" : "Draw"}</td>
      <td style="text-align:right">${e.debit ? formatINR(e.debit) : ""}</td>
      <td style="text-align:right">${e.credit ? formatINR(e.credit) : ""}</td>
      <td>${(e.note || "").replace(/</g, "&lt;")}</td></tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print"); return; }
    w.document.write(`<html><head><title>Personal Statement — ${a.name}</title>
      <style>body{font-family:Georgia,serif;color:#1A1A18;padding:32px}h1{margin:0 0 4px}h2{font-size:13px;color:#68665E;font-weight:400;margin:0 0 16px}
      table{width:100%;border-collapse:collapse;font-size:12px}th,td{border-bottom:1px solid #E5E3DB;padding:6px 8px;text-align:left}
      th{background:#1A1A18;color:#fff}.sum{margin-top:20px;font-weight:bold}.sum span{margin-right:32px}</style></head>
      <body><h1>Personal Account Statement — ${a.name}</h1><h2>Indian Transport Organisation · Period: ${period}</h2>
      ${a.note ? `<p style="font-size:12px;color:#68665E">Note: ${a.note}</p>` : ""}
      <table><thead><tr><th>Date</th><th>Account</th><th>Description</th><th>Type</th><th>Given</th><th>Repaid</th><th>Note</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="7">No entries</td></tr>'}</tbody></table>
      <div class="sum"><span>Outstanding advance owed: ${formatINR(a.advance_out)}</span><span>Total draws: ${formatINR(a.draw_total)}</span></div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  }

  const totalAdvance = accounts.reduce((s, a) => s + (a.advance_out || 0), 0);
  const totalDraw = accounts.reduce((s, a) => s + (a.draw_total || 0), 0);

  return (
    <div data-testid="personal-accounts-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Reconciliation</div>
        <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Personal Accounts</h1>
        <p className="text-sm text-[#68665E] mt-2">
          Personal transfers tallied from bank statements — reimbursable advances/loans (owed back) and personal draws, per person. Kept out of business expenses.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="input-label">From</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-field text-sm" data-testid="pa-start" />
        </div>
        <div>
          <label className="input-label">To</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-field text-sm" data-testid="pa-end" />
        </div>
        {(start || end) && (
          <button onClick={() => { setStart(""); setEnd(""); }} className="btn-ghost text-sm" data-testid="pa-clear">Clear</button>
        )}
        <div className="ml-auto flex gap-6 text-sm">
          <div><div className="text-xs text-[#68665E]">Total advances owed</div><div className="font-bold text-[#C84B31]" data-testid="pa-total-advance">{formatINR(totalAdvance)}</div></div>
          <div><div className="text-xs text-[#68665E]">Total draws</div><div className="font-bold text-[#1A1A18]" data-testid="pa-total-draw">{formatINR(totalDraw)}</div></div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#68665E]" data-testid="pa-loading">Loading…</div>
      ) : (
        <div className="space-y-3">
          {accounts.map((a) => {
            const isOpen = expanded === a.user_id;
            return (
              <div key={a.user_id} className="card-surface" data-testid={`pa-card-${a.user_id}`}>
                <button onClick={() => setExpanded(isOpen ? null : a.user_id)}
                        className="w-full flex items-center gap-4 p-4 text-left" data-testid={`pa-toggle-${a.user_id}`}>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-[#68665E]" /> : <ChevronRight className="w-4 h-4 text-[#68665E]" />}
                  <div className="w-9 h-9 rounded-full bg-[#F5F3EC] flex items-center justify-center"><User className="w-4 h-4 text-[#C84B31]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#1A1A18]">{a.name} {a.role === "admin" && <span className="text-[10px] uppercase tracking-wide text-[#68665E]">· admin</span>}</div>
                    <div className="text-xs text-[#68665E]">{a.count} personal {a.count === 1 ? "entry" : "entries"}</div>
                  </div>
                  <div className="flex gap-6 text-right text-sm shrink-0">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-[#68665E]">Advance owed</div>
                      <div className="font-bold text-[#C84B31]">{formatINR(Math.max(0, a.advance_out))}</div>
                      {a.advance_out < -0.5 && (
                        <div className="text-[10px] font-semibold text-[#2D523B]" data-testid={`pa-overpaid-${a.user_id}`}>Overpaid {formatINR(-a.advance_out)}</div>
                      )}
                    </div>
                    <div><div className="text-[10px] uppercase tracking-wide text-[#68665E]">Draws</div><div className="font-bold text-[#1A1A18]">{formatINR(a.draw_total)}</div></div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-[#E5E3DB] p-4 space-y-4" data-testid={`pa-detail-${a.user_id}`}>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setPayModal({ payee_name: a.name, upi_id: "", amount: "", note: `Advance to ${a.name}`, category: "Personal advance", ref_type: "personal", ref_id: a.user_id, ref_number: a.name })} className="btn-secondary inline-flex items-center gap-1.5 text-sm" data-testid={`pa-pay-${a.user_id}`}>
                        <IndianRupee className="w-4 h-4" /> Pay via UPI
                      </button>
                      {a.advance_out > 0 && (
                        <button onClick={() => openRepay(a)} className="btn-secondary inline-flex items-center gap-1.5 text-sm" data-testid={`pa-repay-${a.user_id}`}>
                          <HandCoins className="w-4 h-4" /> Record repayment
                        </button>
                      )}
                      <button onClick={() => exportExcel(a.user_id)} className="btn-secondary inline-flex items-center gap-1.5 text-sm" data-testid={`pa-export-${a.user_id}`}>
                        <FileSpreadsheet className="w-4 h-4" /> Export Excel
                      </button>
                      <button onClick={() => printStatement(a)} className="btn-secondary inline-flex items-center gap-1.5 text-sm" data-testid={`pa-print-${a.user_id}`}>
                        <Printer className="w-4 h-4" /> Print statement
                      </button>
                    </div>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="input-label">Account note</label>
                        <input value={noteDrafts[a.user_id] || ""} onChange={(e) => setNoteDrafts({ ...noteDrafts, [a.user_id]: e.target.value })}
                               placeholder="e.g. Salary advance to be adjusted next month" className="input-field w-full text-sm" data-testid={`pa-note-${a.user_id}`} />
                      </div>
                      <button onClick={() => saveNote(a.user_id)} className="btn-secondary inline-flex items-center gap-1 text-sm" data-testid={`pa-note-save-${a.user_id}`}>
                        <Save className="w-3.5 h-3.5" /> Save
                      </button>
                    </div>

                    {a.entries.length === 0 ? (
                      <div className="text-sm text-[#68665E]">No personal transfers in this period.</div>
                    ) : (
                      <div className="table-wrap">
                        <table className="w-full">
                          <thead><tr>
                            <th className="table-th">Date</th>
                            <th className="table-th">Account</th>
                            <th className="table-th">Description</th>
                            <th className="table-th">Type</th>
                            <th className="table-th text-right">Given</th>
                            <th className="table-th text-right">Repaid</th>
                            <th className="table-th">Note</th>
                          </tr></thead>
                          <tbody>
                            {a.entries.map((e) => (
                              <tr key={e.id} className="table-row">
                                <td className="table-td nowrap">{formatDate(e.date)}</td>
                                <td className="table-td">{e.account}</td>
                                <td className="table-td">{e.description}</td>
                                <td className="table-td">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-semibold ${e.repayment ? "bg-[#EAF2ED] text-[#2D523B]" : e.kind === "advance" ? "bg-[#FCE9E4] text-[#C84B31]" : "bg-[#E5E3DB] text-[#68665E]"}`}>
                                    {e.repayment ? "Repayment" : e.kind === "advance" ? "Advance/Loan" : "Draw"}
                                  </span>
                                </td>
                                <td className="table-td text-right text-[#8A2526]">{e.debit ? formatINR(e.debit) : ""}</td>
                                <td className="table-td text-right text-[#2F6F4E]">{e.credit ? formatINR(e.credit) : ""}</td>
                                <td className="table-td text-[#68665E]">{e.note}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {repayFor && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="repay-modal">
          <div className="absolute inset-0 bg-[#1A1A18]/60 backdrop-blur-sm" onClick={() => setRepayFor(null)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl rounded-lg overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E3DB]">
              <div>
                <h3 className="font-headings font-bold text-lg text-[#1A1A18]">Record repayment — {repayFor.name}</h3>
                <p className="text-xs text-[#68665E] mt-0.5">Outstanding advance: <span className="font-semibold text-[#C84B31]">{formatINR(repayFor.advance_out)}</span>. Pick the credit row that repaid it.</p>
              </div>
              <button onClick={() => setRepayFor(null)} data-testid="repay-close" className="w-8 h-8 rounded-full hover:bg-[#F5F3EC] flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-[#F0EEE8]">
              {loadingCredits ? (
                <div className="p-6 text-sm text-[#68665E]">Loading credit rows…</div>
              ) : credits.length === 0 ? (
                <div className="p-6 text-sm text-[#68665E]" data-testid="repay-empty">No untallied credit rows available.</div>
              ) : credits.map((c) => (
                <button key={c.id} onClick={() => recordRepayment(c)} data-testid={`repay-credit-${c.id}`}
                        className="w-full text-left px-5 py-3 hover:bg-[#FAF9F6] flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1A1A18] truncate">{c.description || "Credit"}</div>
                    <div className="text-xs text-[#68665E]">{formatDate(c.date)} · {c.account_label}</div>
                  </div>
                  <span className="text-sm font-semibold text-[#2F6F4E] shrink-0">{formatINR(c.credit)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <UpiPayModal open={!!payModal} prefill={payModal || {}} onClose={() => setPayModal(null)} />
    </div>
  );
}
